<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Dialog, DialogContent, DialogTitle } from '~/components/ui/dialog'
import { usePhotoZoom } from '~/composables/usePhotoZoom'
import { photoName, photoUrl } from '~/types/photo'

/**
 * 全站共用相片放大 Dialog — 單例掛在 `app.vue`，由 `usePhotoZoom().open(photo)` 觸發。
 *
 * 尺寸策略（回應「不要超過原圖 size 免得糊掉」）：
 * - Dialog 外框：`min(vw 上限, 原圖寬)`，mobile 85vw / sm+ 60vw
 * - 用 CSS variable + Tailwind arbitrary `[max-width:min(...)]` 走 build-time 靜態 class + runtime 動態值兩全的路
 * - Img `max-height: min(90vh, 原圖高)` 綁在 :style（inline style 過 class specificity 直接覆蓋 shadcn DialogContent 預設 max-w-sm）
 * - `w-auto h-auto` 讓 img 依原比例呈現，永不 upscale
 */
const { isOpen, currentPhoto, close } = usePhotoZoom()

const url = computed(() => photoUrl(currentPhoto.value, 'large'))
const fileName = computed(() => photoName(currentPhoto.value))

/** 圖片原生尺寸 — 用來把 Dialog / img 的 max 卡在原圖大小，不 upscale */
const natural = ref<{ w: number; h: number } | null>(null)

// 換圖就重置，避免用上一張的尺寸把新圖 crop 掉
watch(currentPhoto, () => { natural.value = null })

function onLoad(e: Event) {
  const img = e.target as HTMLImageElement
  natural.value = { w: img.naturalWidth, h: img.naturalHeight }
}

/**
 * DialogContent 尺寸靠 CSS variable 動態傳原圖寬，Tailwind arbitrary class 靜態列在 template；
 * 抽 computed 讓 template 不每 render 重建 style object。
 */
const contentStyle = computed(() => {
  if (!natural.value) return undefined
  return {
    '--nat-w': `${natural.value.w}px`,
    '--nat-h': `${natural.value.h}px`,
  }
})

/** Img 高度上限：不超過 90vh、也不超過原圖高避免 upscale */
const imgStyle = computed(() => {
  if (!natural.value) return undefined
  return { maxHeight: `min(90vh, ${natural.value.h}px)` }
})
</script>

<template>
  <Dialog :open="isOpen" @update:open="(v) => { if (!v) close() }">
    <DialogContent
      :style="contentStyle"
      :class="[
        // 透明底 + 移掉 shadcn 預設 padding / ring / shadow — 讓圖片本身即視覺主體
        'bg-transparent p-0 ring-0 shadow-none',
        natural
          ? 'max-w-[min(85vw,var(--nat-w))] sm:max-w-[min(60vw,var(--nat-w))]'
          : 'max-w-[85vw] sm:max-w-[60vw]',
      ]"
    >
      <DialogTitle class="sr-only">{{ fileName ?? '圖片放大' }}</DialogTitle>
      <img
        v-if="url"
        :src="url"
        :alt="fileName ?? ''"
        :style="imgStyle"
        class="mx-auto h-auto w-auto max-w-full rounded-xl"
        @load="onLoad"
      >
    </DialogContent>
  </Dialog>
</template>
